/**
 * Convert Quill-produced HTML to plain text with deterministic rules.
 * The output is byte-identical to the Python port in
 * `server/emailbison/rendering/render.py:render_html_to_plain_text`.
 */
export function renderHtmlToPlainText(html: string): string {
  // Step 1: normalize block-level tags into newline markers.
  // We use a DOMParser walk for correctness (handles nested tags).
  const parser = new DOMParser()
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
  const out: string[] = []

  function walk(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(node.textContent ?? '')
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    if (tag === 'br') {
      out.push('\n')
      return
    }
    if (tag === 'a') {
      const href = el.getAttribute('href') ?? ''
      const text = el.textContent ?? ''
      out.push(text === href || !href ? text : `${text} (${href})`)
      return
    }
    if (tag === 'li') {
      out.push('- ')
      for (const child of Array.from(el.childNodes)) walk(child)
      out.push('\n')
      return
    }

    const isBlock = [
      'p',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'blockquote',
    ].includes(tag)
    for (const child of Array.from(el.childNodes)) walk(child)
    if (isBlock) out.push('\n\n')
  }

  for (const child of Array.from(doc.body.childNodes)) walk(child)

  // Step 2: normalize non-breaking spaces (U+00A0) to regular spaces. Google
  // Docs's clipboard format uses NBSP between words; if we preserve them, the
  // preview pane can't wrap (NBSP doesn't allow line breaks) and the rendered
  // bytes would have invisible-but-functionally-different whitespace.
  // Then collapse runs of 3+ newlines to exactly two.
  const collapsed = out
    .join('')
    .replace(/\u00A0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')

  // Step 3: trim trailing whitespace.
  return collapsed.replace(/\s+$/, '')
}
